import { Container } from '../../odie';

import { BasicButton } from '../../lib/ui/BasicButton';
import { numberClamp } from '../util/mathTools';
import { BasicText } from './BasicText';
import {
  RichTextHorizontalAlign,
  RichTextVerticalAlign,
} from '@play-co/timestep-core/ui';

// types
//-----------------------------------------------------------------------------
export type NumberListener = (value: number) => void;

export type NumberPickerOptions = {
  value: number;
  min: number;
  max: number;
  width: number;
  height: number;
  increment?: number;
  suffix?: string;
};

// constants
//-----------------------------------------------------------------------------
const arrowImage = 'assets/ui/match2/editor/buttons/button.arrow.png';

/*
    number picker
*/
export class NumberPicker extends Container {
  // private
  //-------------------------------------------------------------------------
  // events
  public onChanged?: NumberListener;
  // input
  private _min: number;
  private _max: number;
  private readonly _suffix?: string;
  // scene
  private _text!: BasicText;
  // state
  private _value!: number;

  // properties
  //-------------------------------------------------------------------------
  public get value(): number {
    return this._value;
  }

  public set value(value: number) {
    // set field
    this._value = numberClamp(value, this._min, this._max);

    // update text
    this._updateText();
  }

  public get max(): number {
    return this._max;
  }

  public set max(value: number) {
    this._max = value;
  }

  public get min(): number {
    return this._min;
  }

  public set min(value: number) {
    this._min = value;
  }

  // init
  //-------------------------------------------------------------------------
  constructor(options: NumberPickerOptions) {
    super();
    this.width = options.width;
    this.height = options.height;
    const increment = options.increment || 1;

    // set fields
    this._min = options.min;
    this._max = options.max;
    this._suffix = options.suffix || '';

    // spawn decrement
    this._spawnDecrement(options, increment);

    // spawn text
    this._spawnText();

    // spawn increment
    this._spawnIncrement(options, increment);

    // set initial value
    this.value = options.value;

    this.setBackgroundColor('rgba(255, 255, 255, 0.1)');
  }

  public static assets(): string[] {
    return [arrowImage];
  }

  // private: updaters
  //-------------------------------------------------------------------------
  private _updateText() {
    // update text with value
    this._text.setText(`${this._value.toString()}${this._suffix}`);
  }

  // private: spawn
  //-------------------------------------------------------------------------
  private _spawnDecrement(options: NumberPickerOptions, increment: number) {
    const button = new BasicButton({
      up: arrowImage,
      width: this.height * 0.8,
      height: this.height,
    });
    button.onTap = () => {
      this.value -= increment;
      this.onChanged?.(this._value);
    };
    button.initListeners();
    this.addChild(button);
    button['_update']();
    button.x = 0;
    button.y = 0;
  }

  private _spawnIncrement(options: NumberPickerOptions, increment: number) {
    const button = new BasicButton({
      up: arrowImage,
      width: this.height * 0.8,
      height: this.height,
    });
    button.scaleX = -1;
    button.onTap = () => {
      this.value += increment;
      this.onChanged?.(this._value);
    };
    button.initListeners();
    this.addChild(button);
    button.x = this.width;
    button.y = 0;
  }

  private _spawnText() {
    // spawn number text
    const text = new BasicText({
      extraStyle: {
        fill: '#fff',
        fontSize: 26,
        // strokeThickness: Math.ceil(options.height * 0.2),
        // lineJoin: 'round',
      },
    });
    text.setTextOpts({
      align: RichTextHorizontalAlign.Center,
      verticalAlign: RichTextVerticalAlign.Center,
      size: 38,
    });
    text.anchorX = 0.5;
    text.anchorY = 0.5;
    text.width = this.width * 0.25;
    text.height = this.height;
    this.addChild(text);

    // add prompt on tap
    text.interactive = true;
    text.setOnTap(() => {
      const result = prompt('NUMBER', this.value.toString());
      const value = parseInt(result || '');
      if (!isNaN(value)) {
        this.value = value;
        this.onChanged?.(value);
      }
    });
    text.x = this.width / 2;
    text.y = this.height / 2 - 5;

    this._text = text;
  }
}
